import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { setCommitteeContext } from '@/utils/committeeContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const committeeSlug = searchParams.get('committee');
  const redirectPath = searchParams.get('redirect');
  const [committeeInfo, setCommitteeInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchCommitteeInfo = async () => {
    if (!committeeSlug) return null;
    try {
      const response = await axios.get(`${API}/public/committees/${committeeSlug}`);
      const committee = response.data?.committee;
      if (committee) {
        setCommitteeContext(committee);
        setCommitteeInfo(committee);
      }
      return committee || null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    fetchCommitteeInfo();
  }, [committeeSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');

      let committeeContext = committeeInfo;
      if (committeeSlug && !committeeContext) {
        committeeContext = await fetchCommitteeInfo();
      }
      
      // Redirect based on role
      const routes = {
        admin: '/admin',
        sponsor: '/sponsor',
        chair: '/review',
        committee: '/review',
        applicant: '/dashboard'
      };
      let targetRoute = routes[user.role] || '/dashboard';

      if (committeeContext?.slug && user.role === 'applicant') {
        targetRoute = redirectPath || `/${committeeContext.slug}/dashboard`;
      }
      if (committeeContext?.id && ['chair', 'co_chair', 'committee'].includes(user.role)) {
        targetRoute = `/committee-dashboard?committee=${committeeContext.id}`;
      }
      
      navigate(targetRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link
            to={committeeSlug ? `/${committeeSlug}` : '/'}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-greek-blue mb-8 transition-colors"
            data-testid="login-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-greek-blue rounded-sm flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-greek-blue">AHEPA Family</h1>
                <p className="text-xs text-slate-500">Scholarship Program</p>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Welcome Back</h2>
            <p className="text-slate-600 mt-2">Sign in to continue your application</p>
            {committeeInfo?.name && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700" data-testid="login-committee-context">
                Signing in to {committeeInfo.name}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm font-semibold tracking-wide uppercase"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <div className="text-center mt-4">
              <Link 
                to="/forgot-password" 
                className="text-sm text-slate-500 hover:text-greek-blue transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="text-center mt-8 text-slate-600">
            Don't have an account?{' '}
            <Link
              to={committeeSlug ? `/signup?committee=${committeeSlug}` : '/signup'}
              className="text-greek-blue font-semibold hover:underline"
              data-testid="login-signup-link"
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
              <Award className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-4xl font-bold mb-4">Excellence in Education</h2>
            <p className="text-white/80 text-lg max-w-md mx-auto">
              Supporting students of Greek heritage in their pursuit of academic excellence since 1922.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
      </div>
    </div>
  );
};

export default LoginPage;
